/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { App, Modal } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import SendEmailIcon from 'src/components/icons/SendEmailIcon'
import Button from 'src/components/ts-components/Button'
import { useCallback, useEffect, useState } from 'react'
import { BillingScheduleItem } from '@sportninja/common/types/Registration'
import req from '@sportninja/common/api/request'

type Props = {
  isOpen: boolean
  onClose: () => void
  defaultEmail?: string
  defaultName?: string
  billing: BillingScheduleItem | null
}

const SendInvoiceModal = ({
  isOpen,
  onClose,
  defaultEmail,
  defaultName,
  billing,
}: Props) => {
  const [isSending, setIsSending] = useState(false)
  const { message } = App.useApp()

  useEffect(() => {
    if (isOpen) {
      setIsSending(false)
    }
  }, [isOpen, defaultEmail])

  const handleClose = useCallback(() => {
    setIsSending(false)
    onClose()
  }, [onClose])

  const handleSend = useCallback(async () => {
    if (isSending) {
      return
    }
    console.log(billing)
    try {
      setIsSending(true)
      await req(`/billing/schedule/${billing?.uid}/send_invoice`, {
        method: 'GET',
      })
      message.success('Invoice sent successfully')
      onClose()
    } finally {
      setIsSending(false)
    }
  }, [billing, isSending, message, onClose])

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      centered
      maskClosable={false}
      zIndex={10020}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
          borderRadius: 16,
          border: `1px solid ${colors.SECONDARY_100}`,
          overflow: 'hidden',
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10019,
        },
        wrapper: {
          zIndex: 10020,
        },
      }}
      css={css`
        .ant-modal-header {
          display: none;
        }
        .ant-modal-body {
          display: flex;
          flex-direction: column;
          padding: 24px;
          gap: 16px;
          background: ${colors.SECONDARY_300};
        }
        .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        `}
      >
        <SendEmailIcon size={48} color={colors.PRIMARY} />
        <Text variant='heading1' weight='semiBold' color={colors.WHITE}>
          Email {billing?.paid ? 'Receipt' : 'Invoice'}
        </Text>
        <div
          css={css`
            align-self: stretch;
            text-align: center;
          `}
        >
          <Text variant='paragraphMedium' weight='regular' color={colors.WHITE}>
            The registration {billing?.paid ? 'receipt' : 'invoice'} will be
            sent to {defaultName} at {defaultEmail}
          </Text>
        </div>
        <div
          css={css`
            align-self: stretch;
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            margin-top: 8px;
          `}
        >
          <Button text='Cancel' onClick={handleClose} variant='secondary' />
          <Button
            text={isSending ? 'SENDING…' : 'SEND'}
            onClick={handleSend}
            disabled={isSending}
            isLoading={isSending}
            variant='primary'
          />
        </div>
      </div>
    </Modal>
  )
}

export default SendInvoiceModal
